import {
  Edge,
  Node,
  EdgeChange,
  NodeChange,
  OnNodesChange,
  OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  XYPosition,
} from "reactflow";
import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { nanoid } from "nanoid/non-secure";
import { getCalculatedValue } from "../Helpers/Calculations";
import {
  viewTypes,
  NodeData,
  OneNotePage,
  CustomProperty,
  values,
  Address,
  Task,
  User,
} from "../types/types";

export type RFState = {
  oneNotePages: OneNotePage[];
  nodes: Node<NodeData>[];
  edges: Edge[];
  isDirty: boolean;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  updateNodeLabel: (nodeId: string, label: string) => void;
  updateNodeTask: (nodeId: string, isTask: boolean, label: string) => void;
  // updateNodeDescription: (nodeId: string, description: string) => void;
  updatePresentationOrder: (nodeId: string, presentationOrder: string) => void;
  updateNodeData: (nodeId: string, data: Partial<NodeData>) => void;
  addChildNode: (parentNode: Node, position: XYPosition) => void;
  updateParentNode: (
    childNodeId: string,
    newParentNodeId: string,
    childNode: Node,
    newParentNode: Node
  ) => void;
  setOneNotePages: (oneNotePages: OneNotePage[]) => void;
  setNodes: (nodes: Node<NodeData>[]) => void;
  setEdges: (edges: Edge[]) => void;
  resetStore: () => void;
  updateNodeNotes: (
    nodeId: string,
    notes: string,
    links: { title: string; url: string }[]
  ) => void;
  selectedNodeId: string | null;
  setSelectedNodeId: (nodeId: string | null) => void;
  selectedNode: Node<NodeData> | null;
  setSelectedNode: (node: Node<NodeData>) => void;
  focusValue: string;
  setFocusValue: (value: string) => void;
  showValues: boolean;
  setShowValues: (value: boolean) => void;
  updateValues: string;
  setUpdateValues: (value: string) => void;
  snapToGrid: boolean;
  toggleSnapToGrid: () => void;
  showMiniMap: boolean;
  toggleShowMiniMap: () => void;
  updateNodeLabelWithEmoji: (
    nodeId: string,
    emoji: string,
    position: number
  ) => void;
  hideChildNodes: (nodeId: string) => void;
  showChildNodes: (nodeId: string, showAllLevels: boolean) => void;
  hideChildNode: (nodeId: string) => void;
  showChildNode: (nodeId: string) => void;
  addEdge: (edge: Edge) => void;
  setIsDirty: (isDirty: boolean) => void;
  addCrossLine: (sourceId: string, targetId: string) => void;
  selectedEdgeId: string | null;
  setSelectedEdgeId: (edgeId: string | null) => void;
  isPresentationMode: boolean;
  setIsPresentationMode: (isPresentationMode: boolean) => void;
  setEdgeLabel: (edgeId: string, label: string) => void;
  showNotesModal: boolean;
  setShowNotesModal: (show: boolean) => void;
  currentNotes: string | "";
  setCurrentNotes: (notes: string) => void;
  aiResponse: string | "";
  setAIResponse: (response: string) => void;
  currentLinks: { title: string; url: string }[];
  setCurrentLinks: (links: { title: string; url: string }[]) => void;
  currentNodeLabel: string;
  setCurrentNodeLabel: (label: string) => void;
  currentView: any;
  setCurrentView: (view: any) => void;
  updatePriority: (nodeId: string, priority: string) => void;
  updateValue: (nodeId: string, value: string) => void;
  calculateSummary: (nodeId: string) => string;
  updateNodeCustomProperties: (
    nodeId: string,
    customProperties: CustomProperty[]
  ) => void;
  updateNodeRollupValues: (
    nodeId: string,
    customProperties: CustomProperty[]
  ) => void;
  useLocalStorage: boolean;
  setUseLocalStorage: (useLocalStorage: boolean) => void;
  presentWithShowHideButtons: boolean;
  setPresentWithShowHideButtons: (presentWithShowHideButtons: boolean) => void;
  sourceOptions: string[];
  setSourceOptions: (sourceOptions: string[]) => void;
  doRollup: boolean;
  setDoRollup: (active: boolean) => void;
  currentFlowId: string | "";
  setCurrentFlowId: (flowId: string) => void;
  showViewSelector: boolean;
  setShowViewSelector: (show: boolean) => void;
  ideationMode: boolean;
  setIdeationMode: (mode: boolean) => void;
  ms365Mode: boolean;
  setMS365Mode: (mode: boolean) => void;
  aiMode: boolean;
  setAIMode: (mode: boolean) => void;
  detailsMode: boolean;
  setDetailsMode: (mode: boolean) => void;
  focusFilterStatuses: string[];
  setFocusFilterStatuses: (statuses: string[]) => void;
  currency: string;
  setCurrency: (currency: string) => void;
  tabModeActive: boolean;
  setTabModeActive: (active: boolean) => void;
  tabModeParentId: string | null;
  setTabModeParentId: (parentId: string | null) => void;
  addChildNodeInTabMode: (parentId: string) => string;
  redistributeChildNodes: (parentId: string) => void;
  redistributeAllChildNodes: (startNodeId: string) => void;
  redistributeAllChildNodesInStarLayout: (startNodeId: string) => void;
  redistributeAllChildNodesRadial: (startNodeId: string) => void;
  scaleChildNodePositions: (parentId: string, scaleFactor: number) => void;
  scaleAllChildNodePositions: (
    startNodeId: string,
    scaleFactor: number
  ) => void;
  childNodeDistance: number;
  increaseChildNodeDistance: () => void;
  decreaseChildNodeDistance: () => void;
  increaseGlobalNodeDistance: () => void;
  decreaseGlobalNodeDistance: () => void;
  user: User | null;
  setUser: (user: User | null) => void;
  toastMessage: { severity: string; summary: string; detail: string } | null;
  showToast: (severity: string, summary: string, detail: string) => void;
  clearToast: () => void;
};

const recalculateRollup = async () => {
  const { nodes, setNodes } = useStore.getState();
  const updatedNodes = nodes.map((node) => ({
    ...node,
    data: { ...node.data, rollupValues: [] },
  }));
  setNodes(updatedNodes);

  const calculateRollup = () => {
    // Build adjacency list for parent-child relationships
    const adjacencyList = nodes.reduce((acc, node) => {
      if (node.parentId) {
        if (!acc[node.parentId]) acc[node.parentId] = [];
        acc[node.parentId].push(node.id);
      }
      return acc;
    }, {} as Record<string, string[]>);

    // Create a map to store rollup values
    const rollupMap = new Map<string, values[]>();

    // Initialize rollup values for nodes without children
    const initializeRollupValues = (node: Node) => {
      const rollupValues: values[] = [];
      const customProps = node.data.customProperties;

      // console.log('initialize:', node.data.label);

      if (customProps) {
        customProps.forEach((prop: CustomProperty) => {
          if (
            prop.visible &&
            ["number", "string", "boolean", "date"].includes(prop.type)
          ) {
            rollupValues.push({ title: prop.name, value: prop.value });
          }
          // Handle currency type
          if (
            prop.visible &&
            prop.type === "currency" &&
            prop.value &&
            prop.value !== "" &&
            prop.value !== "0"
          ) {
            const currency = useStore.getState().currency || "EUR";
            rollupValues.push({
              title: prop.name,
              value: `${prop.value} ${currency}`,
            });
          }
          // Usage
          if (prop.visible && prop.type === "calculated") {
            const currency = useStore.getState().currency || "EUR";
            const rollupValue = getCalculatedValue(prop, customProps, currency);
            if (rollupValue) {
              rollupValues.push(rollupValue);
            }
          }
        });
      }

      rollupMap.set(node.id, rollupValues);
    };

    // Recursive function to calculate rollup values for a node
    const calculateNodeRollup = (nodeId: string): values[] => {
      if (rollupMap.has(nodeId)) {
        return rollupMap.get(nodeId) || [];
      }

      const children = adjacencyList[nodeId] || [];
      const aggregatedRollup: Record<string, number> = {};
      const currencyProperties = new Set<string>();

      // Aggregate rollup values from child nodes
      children.forEach((childId) => {
        const childRollup = calculateNodeRollup(childId);
        childRollup.forEach((rollup) => {
          if (!aggregatedRollup[rollup.title]) {
            aggregatedRollup[rollup.title] = 0;
          }
          // Check if this property has a currency suffix
          const valueStr = rollup.value || "0";
          if (
            valueStr.includes(" EUR") ||
            valueStr.includes(" USD") ||
            valueStr.includes(" GBP") ||
            valueStr.match(/\s[A-Z]{3}(\s|Σ|$)/)
          ) {
            currencyProperties.add(rollup.title);
          }
          aggregatedRollup[rollup.title] += parseFloat(rollup.value || "0");
        });
      });

      // Add custom properties to rollup values
      const node = nodes.find((n) => n.id === nodeId);
      const rollupValues: values[] = [];
      if (node) {
        const currency = useStore.getState().currency || "EUR";
        Object.entries(aggregatedRollup).forEach(([title, aggregatedValue]) => {
          const isCurrency = currencyProperties.has(title);
          rollupValues.push({
            title,
            value: isCurrency
              ? `${aggregatedValue} ${currency} Σ`
              : aggregatedValue.toString() + " Σ",
          });
        });
      }

      rollupMap.set(nodeId, rollupValues);
      return rollupValues;
    };

    // Start by initializing rollup values for nodes without children
    nodes.forEach((node) => {
      if (!adjacencyList[node.id]) {
        initializeRollupValues(node);
      }
    });

    // Calculate rollup values for all nodes
    nodes.forEach((node) => {
      calculateNodeRollup(node.id);
    });

    // Update nodes with calculated rollup values
    const updatedNodes = nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        rollupValues: rollupMap.get(node.id) || [],
      },
    }));

    setNodes(updatedNodes);
  };

  calculateRollup();
};

const useStore = createWithEqualityFn<RFState>((set, get) => ({
  oneNotePages: [],
  nodes: [],
  edges: [],
  isDirty: false,
  onNodesChange: (changes: NodeChange[]) => {
    // Filter out any attempts to delete the "root" node
    let filteredChanges = changes.filter(
      (change) => !(change.type === "remove" && change.id === "root")
    );

    if (filteredChanges.some((change) => change.type === "remove")) {
      const { edges, nodes } = get();

      // Build adjacency list for edges
      const adjacencyList = edges.reduce((acc, edge) => {
        if (!acc[edge.source]) acc[edge.source] = [];
        acc[edge.source].push(edge.target);
        return acc;
      }, {} as Record<string, string[]>);

      // Helper function to find all descendants of a node
      const findDescendants = (nodeId: string): string[] => {
        const descendants: string[] = [];
        const queue: string[] = [nodeId]; // Start with the parent node

        while (queue.length > 0) {
          const current = queue.shift()!;
          const children = adjacencyList[current] || [];
          descendants.push(current);
          queue.push(...children);
        }

        return descendants;
      };

      // Collect all nodes to delete
      const nodesToDelete = new Set<string>();
      filteredChanges
        .filter((change) => change.type === "remove")
        .forEach((change) => {
          const descendants = findDescendants((change as { id: string }).id);
          descendants.forEach((descendant) => nodesToDelete.add(descendant));
        });

      // Filter out nodes and edges to be deleted
      const remainingNodes = nodes.filter(
        (node) => !nodesToDelete.has(node.id)
      );
      const remainingEdges = edges.filter(
        (edge) =>
          !nodesToDelete.has(edge.source) && !nodesToDelete.has(edge.target)
      );

      // Update the state
      set((state) => ({
        nodes: remainingNodes,
        edges: remainingEdges,
      }));
    } else {
      // Apply changes if no node deletions are involved

      filteredChanges = changes.filter((change) => change.type !== "remove");

      set((state) => ({
        nodes: applyNodeChanges(filteredChanges, get().nodes),
      }));
    }
    if (get().doRollup) {
      recalculateRollup();
    }
    set({ isDirty: true });
  },
  onEdgesChange: (changes: EdgeChange[]) => {
    // Filter out any remove changes to prevent edge deletion
    // console.log(changes);
    const filteredChanges = changes.filter((change) => {
      const edge = get().edges.find(
        (e) => e.id === (change as { id: string }).id
      );
      //alert(edge?.type);
      return change.type !== "remove" || edge?.type === "crossline";
    });
    set((state) => ({
      edges: applyEdgeChanges(filteredChanges, get().edges),
    }));
    set({ isDirty: true });
  },
  updateNodeLabel: (nodeId: string, label: string) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, label };
        }
        return node;
      }),
    }));
  },
  updateNodeTask: (nodeId: string, isTask: boolean, label: string) => {
    let task: Task = {
      id: nodeId,
      nodeTitle: label,
      title: label,
      status: "none",
      summary: "",
      priority: "Normal",
      tags: "",
      assignee: "",
      estimate: 0,
      rankId: 0,
      color: "",
      className: "",
      startDate: undefined,
      endDate: undefined,
    };
    if (!isTask) {
      task = {} as Task;
    }

    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, task };
        }
        return node;
      }),
    }));
  },
  // updateNodeDescription: (nodeId: string, description: string) => {
  //   set((state) => ({
  //     nodes: get().nodes.map((node) => {
  //       if (node.id === nodeId) {
  //         node.data = { ...node.data, description };
  //       }
  //       return node;
  //     }),
  //   }));
  // },
  updatePresentationOrder: (nodeId: string, presentationOrder: string) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, presentationOrder };
        }
        return node;
      }),
    }));
  },
  updateNodeData: (nodeId: string, data: Partial<NodeData>) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, ...data };
        }
        return node;
      }),
    }));
  },
  updateNodeLabelWithEmoji: (
    nodeId: string,
    emoji: string,
    position: number
  ) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          const newLabel =
            node.data.label.slice(0, position) +
            emoji +
            node.data.label.slice(position);
          node.data = { ...node.data, label: newLabel };
        }
        return node;
      }),
    }));
  },
  addChildNode: (parentNode: Node, position: XYPosition) => {
    const newNodeId = nanoid();
    const newNode: Node<NodeData> = {
      id: newNodeId,
      type: "mindmap",
      dragging: false,
      selected: false,
      data: {
        label: "Point",
        icon: "",
        object: "",
        objectType: "",
        objectWidth: 0,
        objectHeight: 0,
        notes: "",
        complete: false,
        status: "none",
        flagged: "none",
        color: parentNode.data.color,
        backgroundColor: parentNode.data.backgroundColor,
        presentationOrder: "",
        priority: "",
        links: [],
        value: "0",
        values: [],
        rollupValues: [],
        customProperties: [],
        event: {
          id: "",
          nodeTitle: "",
          title: "",
          description: "",
          date: undefined,
          icon: "",
          color: "",
        },
        address: {
          id: "",
          nodeTitle: "",
          name: "",
          street: "",
          zip_code: "",
          city: "",
          Phone: "",
          email: "",
          vat: "",
          employees: "",
          notes: "",
          latitude: undefined,
          longitude: undefined,
        },
        task: {
          id: "",
          nodeTitle: "",
          title: "",
          status: "",
          summary: "",
          priority: "",
          tags: "",
          assignee: "",
          estimate: 0,
          rankId: 0,
          color: "",
          className: "",
          startDate: undefined,
          endDate: undefined,
        },
        ms365Document: undefined,
      },
      position,
      dragHandle: ".dragHandle",
      parentId: parentNode.id,
      style: {
        backgroundColor: parentNode.data.backgroundColor,
      },
    };

    const newEdge = {
      id: nanoid(),
      type:
        parentNode.id === "root" || parentNode.type === "center"
          ? "center"
          : "mindmap",
      source: parentNode.id,
      target: newNode.id,
    };

    // Mark the new node as selected
    newNode.selected = true;

    set((state) => ({
      nodes: [...state.nodes, newNode],
      edges: [...state.edges, newEdge],
      selectedNode: newNode,
      selectedNodeId: newNode.id,
    }));
  },
  updateParentNode: (
    childNodeId: string,
    newParentNodeId: string,
    childNode: Node,
    newParentNode: Node
  ) => {
    const { nodes, edges } = get();

    // Remove the old edge
    const updatedEdges = edges.filter((edge) => edge.target !== childNodeId);

    // Add the new edge
    const newEdge: Edge = {
      id: nanoid(),
      type: newParentNodeId === "root" ? "center" : "mindmap",
      source: newParentNodeId,
      target: childNodeId,
    };

    // Update the parentNode property of the child node
    const updatedNodes = nodes.map((node) => {
      if (node.id === childNodeId) {
        return {
          ...node,
          parentId: newParentNodeId, // was parentNode
          data: {
            label: childNode.data.label,
            icon: childNode.data.icon,
            object: childNode.data.object,
            objectType: childNode.data.objectType,
            objectWidth: childNode.data.objectWidth || 0,
            objectHeight: childNode.data.objectHeight || 0,
            notes: childNode.data.notes,
            complete: childNode.data.complete,
            status: childNode.data.status || "none",
            flagged: childNode.data.flagged,
            color: newParentNode.data.color,
            backgroundColor: newParentNode.data.backgroundColor,
            links: childNode.data.links || [],
            priority: childNode.data.priority || "",
            presentationOrder: childNode.data.presentationOrder || "",
            value: childNode.data.value || "0",
            values: childNode.data.values || [],
            rollupValues: childNode.data.rollupValues || [],
            customProperties: childNode.data.customProperties || [],
            event: childNode.data.event,
            address: childNode.data.address,
            task: childNode.data.task,
            ms365Document: childNode.data.ms365Document,
          },
          style: {
            backgroundColor: newParentNode.data.backgroundColor,
          },
        };
      }
      return node;
    });

    set((state) => ({
      nodes: updatedNodes,
      edges: [...updatedEdges, newEdge],
    }));

    recalculateRollup();
  },
  setOneNotePages: (oneNotePages: OneNotePage[]) => set({ oneNotePages }),
  setNodes: (nodes: Node<NodeData>[]) => set({ nodes }),
  setEdges: (edges: Edge[]) => set({ edges }),
  resetStore: () => {
    set({
      nodes: [],
      edges: [],
    });
  },
  updateNodeNotes: (
    nodeId: string,
    notes: string,
    links: { title: string; url: string }[]
  ) => {
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, notes, links } }
          : node
      ),
    }));
  },
  selectedNodeId: null,
  setSelectedNodeId: (nodeId: string | null) =>
    set((state) => ({
      selectedNodeId: nodeId,
      nodes: state.nodes.map((node) => ({
        ...node,
        selected: node.id === nodeId,
      })),
    })),
  selectedNode: null,
  setSelectedNode: (node: Node<NodeData> | null) => set({ selectedNode: node }),
  focusValue: "",
  setFocusValue: (value: string) => set({ focusValue: value }),
  showValues: false,
  setShowValues: (value: boolean) => set({ showValues: value }),
  updateValues: "",
  setUpdateValues: (value: string) => set({ updateValues: value }),
  snapToGrid: false,
  toggleSnapToGrid: () => set((state) => ({ snapToGrid: !state.snapToGrid })),
  showMiniMap: false,
  toggleShowMiniMap: () =>
    set((state) => ({ showMiniMap: !state.showMiniMap })),
  hideChildNodes: (nodeId: string) => {
    const { nodes } = get();
    const hideDescendants = (parentId: string) => {
      const updatedNodes = nodes.map((node) => {
        if (node.parentId === parentId) {
          // was parentNode
          node.hidden = true;
          hideDescendants(node.id);
        }
        return node;
      });
      return updatedNodes;
    };
    const updatedNodes = hideDescendants(nodeId);
    set({ nodes: updatedNodes });
  },
  showChildNode: (nodeId: string) => {
    const { nodes } = get();
    const updatedNodes = nodes.map((node) => {
      if (node.id === nodeId) {
        node.hidden = false;
      }
      return node;
    });
    set({ nodes: updatedNodes });
  },
  hideChildNode: (nodeId: string) => {
    const { nodes } = get();
    const updatedNodes = nodes.map((node) => {
      if (node.id === nodeId) {
        if (node.type !== "center") {
          node.hidden = true;
        }
      }
      return node;
    });
    set({ nodes: updatedNodes });
  },
  showChildNodes: (nodeId: string, showAllLevels: boolean = false) => {
    const { nodes } = get();
    const showDescendants = (parentId: string) => {
      const updatedNodes = nodes.map((node) => {
        if (node.parentId === parentId) {
          // was parentNode
          node.hidden = false;
          if (showAllLevels) {
            showDescendants(node.id);
          }
        }
        return node;
      });
      return updatedNodes;
    };
    const updatedNodes = showDescendants(nodeId);
    set({ nodes: updatedNodes });
    setTimeout(() => {
      document.getSelection()?.removeAllRanges();
    }, 50);
  },
  addEdge: (edge: Edge) => {
    set((state) => ({
      edges: [...state.edges, edge],
    }));
  },
  setIsDirty: (isDirty: boolean) => set({ isDirty }),
  addCrossLine: (sourceId: string, targetId: string) => {
    const newEdge: Edge = {
      id: nanoid(),
      type: "crossline",
      source: sourceId,
      target: targetId,
      label: "",
    };
    set((state) => ({
      edges: [...state.edges, newEdge],
    }));
  },
  selectedEdgeId: null,
  setSelectedEdgeId: (edgeId: string | null) => set({ selectedEdgeId: edgeId }),
  isPresentationMode: false,
  setIsPresentationMode: (isPresentationMode: boolean) =>
    set({ isPresentationMode }),
  presentWithShowHideButtons: false,
  setPresentWithShowHideButtons: (presentWithShowHideButtons: boolean) =>
    set({ presentWithShowHideButtons }),
  setEdgeLabel: (edgeId: string, label: string) => {
    set((state) => ({
      edges: state.edges.map((edge) =>
        edge.id === edgeId ? { ...edge, label } : edge
      ),
    }));
  },
  currentNotes: "",
  setCurrentNotes: (notes: string) => set({ currentNotes: notes }),
  aiResponse: "",
  setAIResponse: (response: string) => set({ aiResponse: response }),
  currentLinks: [],
  setCurrentLinks: (links: { title: string; url: string }[]) =>
    set({ currentLinks: links }),
  currentView: viewTypes.FocusMap,
  setCurrentView: (view: any) => set({ currentView: view }),
  showNotesModal: false,
  setShowNotesModal: (show: boolean) => set({ showNotesModal: show }),
  updatePriority: (nodeId: string, priority: string) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, priority };
        }
        return node;
      }),
    }));
  },
  updateValue: (nodeId: string, value: string) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, value };
        }
        return node;
      }),
    }));
    recalculateRollup();
  },
  calculateSummary: (nodeId: string) => {
    const { nodes } = get();
    const calculateSum = (nodeId: string): number => {
      const adjacencyList = nodes.reduce((acc, node) => {
        if (node.parentId && !acc[node.parentId]) acc[node.parentId] = [];
        if (node.parentId) {
          acc[node.parentId].push(node.id);
        }
        return acc;
      }, {} as Record<string, string[]>);

      const findDescendants = (nodeId: string): string[] => {
        const descendants: string[] = [];
        const queue: string[] = [nodeId];

        while (queue.length > 0) {
          const current = queue.shift()!;
          const children = adjacencyList[current] || [];
          descendants.push(current);
          queue.push(...children);
        }

        return descendants;
      };

      const descendants = findDescendants(nodeId);
      const sum = descendants.reduce((acc, id) => {
        const node = nodes.find((node) => node.id === id);
        return acc + (node?.data.value ? parseFloat(node.data.value) : 0);
      }, 0);

      return sum;
    };

    const sum = calculateSum(nodeId);
    console.log(`Sum of values for node ${nodeId} and its descendants: ${sum}`);
    return sum.toString();
  },
  updateNodeCustomProperties: (
    nodeId: string,
    customProperties: CustomProperty[]
  ) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          node.data = {
            ...node.data,
            customProperties,
            values: customProperties
              .map((prop: CustomProperty) => {
                if (prop.visible) {
                  if (prop.type === "calculated") {
                    const currency = useStore.getState().currency || "EUR";
                    const value = getCalculatedValue(
                      prop,
                      customProperties,
                      currency
                    );
                    return value || { title: prop.name, value: "0" };
                  } else {
                    // Only return values that are actually set (non-empty)
                    const hasValue =
                      prop.type === "boolean"
                        ? true // boolean always has a value (true/false)
                        : prop.value !== "" &&
                          prop.value !== null &&
                          prop.value !== undefined;

                    if (hasValue) {
                      let displayValue = prop.value;
                      // Add currency suffix for currency type properties
                      if (prop.type === "currency") {
                        const currency = useStore.getState().currency || "EUR";
                        displayValue = `${prop.value} ${currency}`;
                      }
                      return { title: prop.name, value: displayValue };
                    } else {
                      return { title: prop.name, value: "0" };
                    }
                  }
                } else {
                  return { title: prop.name, value: "0" };
                }
              })
              .filter((value: values) => value.value !== "0") as values[] | [],
          };
        }
        return node;
      }),
    }));
    //get().setUpdateValues(nanoid());
  },
  updateNodeRollupValues: (
    nodeId: string,
    customProperties: CustomProperty[]
  ) => {
    set((state) => ({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          const rollupValues: values[] = [];
          customProperties.forEach((prop) => {
            if (prop.value) {
              rollupValues.push({ title: prop.name, value: prop.value });
              // }
            }
          });
          node.data = { ...node.data, rollupValues };
        }
        return node;
      }),
    }));
  },
  useLocalStorage: false,
  setUseLocalStorage: (useLocal: boolean) => set({ useLocalStorage: useLocal }),
  sourceOptions: [],
  setSourceOptions: (sourceOptions: string[]) => set({ sourceOptions }),
  doRollup: false,
  setDoRollup: (active: boolean) => {
    set({ doRollup: active });
    if (active) {
      recalculateRollup();
    }
  },
  currentFlowId: "",
  setCurrentFlowId: (flowId: string) => set({ currentFlowId: flowId }),
  showViewSelector: false,
  setShowViewSelector: (value: boolean) => set({ showViewSelector: value }),
  ideationMode: false,
  setIdeationMode: (value: boolean) => set({ ideationMode: value }),
  ms365Mode: false,
  setMS365Mode: (value: boolean) => set({ ms365Mode: value }),
  aiMode: false,
  setAIMode: (value: boolean) => set({ aiMode: value }),
  detailsMode: false,
  setDetailsMode: (value: boolean) => set({ detailsMode: value }),
  currentNodeLabel: "",
  setCurrentNodeLabel: (label: string) => set({ currentNodeLabel: label }),
  focusFilterStatuses: [],
  setFocusFilterStatuses: (statuses: string[]) =>
    set({ focusFilterStatuses: statuses }),
  currency: "EUR",
  setCurrency: (currency: string) => set({ currency }),
  tabModeActive: false,
  setTabModeActive: (active: boolean) => set({ tabModeActive: active }),
  tabModeParentId: null,
  setTabModeParentId: (parentId: string | null) =>
    set({ tabModeParentId: parentId }),
  childNodeDistance: 160,
  scaleChildNodePositions: (parentId: string, scaleFactor: number) => {
    const { nodes, setNodes } = get();
    const children = nodes.filter((n) => n.parentId === parentId);
    if (children.length === 0) return;

    const updatedNodes = nodes.map((node) => {
      if (node.parentId === parentId) {
        // Scale the position proportionally
        return {
          ...node,
          position: {
            x: node.position.x * scaleFactor,
            y: node.position.y * scaleFactor,
          },
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  scaleAllChildNodePositions: (startNodeId: string, scaleFactor: number) => {
    const { nodes, setNodes } = get();

    console.log("📐 scaleAllChildNodePositions called:", {
      startNodeId,
      scaleFactor,
      totalNodes: nodes.length,
    });

    // Build a map of parent to children
    const childrenMap = new Map<string, Node[]>();
    nodes.forEach((node) => {
      if (node.parentId) {
        if (!childrenMap.has(node.parentId)) {
          childrenMap.set(node.parentId, []);
        }
        childrenMap.get(node.parentId)!.push(node);
      }
    });

    console.log("📊 Children map built:", {
      directChildren: (childrenMap.get(startNodeId) || []).length,
    });

    // Collect all descendants to scale
    const nodesToScale = new Set<string>();
    const collectDescendants = (nodeId: string) => {
      const children = childrenMap.get(nodeId) || [];
      children.forEach((child) => {
        nodesToScale.add(child.id);
        collectDescendants(child.id);
      });
    };
    collectDescendants(startNodeId);

    console.log("🎯 Nodes to scale:", nodesToScale.size);

    // Scale all descendant positions
    const updatedNodes = nodes.map((node) => {
      if (nodesToScale.has(node.id)) {
        const oldPos = node.position;
        const newPos = {
          x: node.position.x * scaleFactor,
          y: node.position.y * scaleFactor,
        };
        console.log(`  Scaling ${node.id}:`, {
          old: oldPos,
          new: newPos,
        });
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  increaseChildNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    set({ childNodeDistance: Math.min(currentDistance + 20, 400) });
  },
  decreaseChildNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    set({ childNodeDistance: Math.max(currentDistance - 20, 80) });
  },
  increaseGlobalNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    const newDistance = Math.min(currentDistance + 20, 400);
    set({ childNodeDistance: newDistance });

    // Re-layout all parent nodes
    const { nodes, redistributeAllChildNodes } = get();
    // Find all root nodes (nodes without parents)
    const rootNodes = nodes.filter((n) => !n.parentId);
    rootNodes.forEach((rootNode) => {
      redistributeAllChildNodes(rootNode.id);
    });
  },
  decreaseGlobalNodeDistance: () => {
    const currentDistance = get().childNodeDistance;
    const newDistance = Math.max(currentDistance - 20, 80);
    set({ childNodeDistance: newDistance });

    // Re-layout all parent nodes
    const { nodes, redistributeAllChildNodes } = get();
    // Find all root nodes (nodes without parents)
    const rootNodes = nodes.filter((n) => !n.parentId);
    rootNodes.forEach((rootNode) => {
      redistributeAllChildNodes(rootNode.id);
    });
  },
  addChildNodeInTabMode: (parentId: string) => {
    const { nodes, edges, addChildNode, childNodeDistance } = get();
    const parentNode = nodes.find((n) => n.id === parentId);
    if (!parentNode) return "";

    // Find children count to determine position
    const childrenCount = nodes.filter((n) => n.parentId === parentId).length;
    const angle = 270 + childrenCount * 60; // Start from top (270°), go clockwise
    const distance = childNodeDistance;
    const radians = (angle * Math.PI) / 180;

    // Get parent dimensions
    const parentWidth =
      (parentNode as any).width || (parentNode as any).measured?.width || 100;
    const parentHeight =
      (parentNode as any).height || (parentNode as any).measured?.height || 40;

    // Follow same pattern as getChildNodePosition in flow.tsx
    const newPosition = {
      x: Math.cos(radians) * distance + parentWidth / 2,
      y: Math.sin(radians) * distance + parentHeight / 2,
    };

    addChildNode(parentNode, newPosition);

    // Get the newly created node ID
    const newNodes = get().nodes;
    const newNode = newNodes[newNodes.length - 1];

    return newNode.id;
  },
  redistributeChildNodes: (parentId: string) => {
    const { nodes, setNodes, childNodeDistance } = get();
    const parentNode = nodes.find((n) => n.id === parentId);
    if (!parentNode) return;

    const children = nodes.filter((n) => n.parentId === parentId);
    if (children.length === 0) return;

    const distance = childNodeDistance;
    const angleStep = 360 / children.length;

    // Get parent dimensions
    const parentWidth =
      (parentNode as any).width || (parentNode as any).measured?.width || 100;
    const parentHeight =
      (parentNode as any).height || (parentNode as any).measured?.height || 40;

    const updatedNodes = nodes.map((node) => {
      const childIndex = children.findIndex((c) => c.id === node.id);
      if (childIndex === -1) return node;

      const angle = 270 + childIndex * angleStep; // Start from top (270°), go clockwise
      const radians = (angle * Math.PI) / 180;

      // Follow same pattern as getChildNodePosition in flow.tsx
      const relativePos = {
        x: Math.cos(radians) * distance + parentWidth / 2,
        y: Math.sin(radians) * distance + parentHeight / 2,
      };

      return {
        ...node,
        position: relativePos,
      };
    });

    setNodes(updatedNodes);
  },
  redistributeAllChildNodes: (startNodeId: string) => {
    const { nodes, setNodes, childNodeDistance } = get();

    // Find the starting node
    const startNode = nodes.find((n) => n.id === startNodeId);
    if (!startNode) return;

    // Build a map of parent to children
    const childrenMap = new Map<string, Node[]>();
    nodes.forEach((node) => {
      if (node.parentId) {
        if (!childrenMap.has(node.parentId)) {
          childrenMap.set(node.parentId, []);
        }
        childrenMap.get(node.parentId)!.push(node);
      }
    });

    // Recursively layout nodes starting from root
    const updatedPositions = new Map<string, { x: number; y: number }>();

    const layoutChildren = (parentId: string) => {
      const children = childrenMap.get(parentId);
      if (!children || children.length === 0) return;

      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode) return;

      const distance = childNodeDistance;
      const angleStep = 360 / children.length;

      // Get parent dimensions
      const parentWidth =
        (parentNode as any).width || (parentNode as any).measured?.width || 100;
      const parentHeight =
        (parentNode as any).height ||
        (parentNode as any).measured?.height ||
        40;

      children.forEach((child, index) => {
        const angle = 270 + index * angleStep;
        const radians = (angle * Math.PI) / 180;

        const relativePos = {
          x: Math.cos(radians) * distance + parentWidth / 2,
          y: Math.sin(radians) * distance + parentHeight / 2,
        };

        updatedPositions.set(child.id, relativePos);

        // Recursively layout this child's children
        layoutChildren(child.id);
      });
    };

    // Start layout from the selected node
    layoutChildren(startNode.id);

    // Apply all position updates
    const updatedNodes = nodes.map((node) => {
      const newPos = updatedPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  redistributeAllChildNodesInStarLayout: (startNodeId: string) => {
    const { nodes, setNodes, childNodeDistance } = get();
    console.log(
      "🎯 Layout starting for node:",
      startNodeId,
      "- Total nodes:",
      nodes.length
    );

    // Find the starting node
    const startNode = nodes.find((n) => n.id === startNodeId);
    if (!startNode) {
      console.warn("⚠️ Start node not found:", startNodeId);
      return;
    }

    // Build a map of parent to children
    const childrenMap = new Map<string, Node[]>();
    nodes.forEach((node) => {
      if (node.parentId) {
        if (!childrenMap.has(node.parentId)) {
          childrenMap.set(node.parentId, []);
        }
        childrenMap.get(node.parentId)!.push(node);
      }
    });

    console.log(
      "🎯 Children map built. Root has",
      (childrenMap.get(startNodeId) || []).length,
      "children"
    );

    // Calculate depth of each node for distance scaling
    const nodeDepths = new Map<string, number>();
    const calculateDepth = (nodeId: string, depth: number) => {
      nodeDepths.set(nodeId, depth);
      const children = childrenMap.get(nodeId) || [];
      children.forEach((child) => calculateDepth(child.id, depth + 1));
    };
    calculateDepth(startNodeId, 0);

    const updatedPositions = new Map<string, { x: number; y: number }>();

    // Hierarchical radial layout with sector allocation
    const layoutChildren = (
      parentId: string,
      parentAngle: number,
      angleSpan: number,
      depth: number,
      parentAbsolutePos: { x: number; y: number }
    ) => {
      const children = childrenMap.get(parentId);
      if (!children || children.length === 0) return;

      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode) return;

      // Moderate distance scaling for better readability
      const baseDistance = childNodeDistance;
      const distance = baseDistance * Math.pow(1.6, depth);
      const angleStep = angleSpan / children.length;

      children.forEach((child, index) => {
        // Distribute children within their allocated angle sector
        const startAngle = parentAngle - angleSpan / 2;
        const childAngle = startAngle + (index + 0.5) * angleStep;
        const radians = (childAngle * Math.PI) / 180;

        // Calculate absolute position from center (not relative to parent)
        const absolutePos = {
          x: Math.cos(radians) * distance,
          y: Math.sin(radians) * distance,
        };

        updatedPositions.set(child.id, absolutePos);

        // Recursively layout this child's children
        layoutChildren(child.id, childAngle, angleStep, depth + 1, absolutePos);
      });
    };

    // For root level, allocate full 360° divided among direct children
    const rootChildren = childrenMap.get(startNodeId);
    if (rootChildren && rootChildren.length > 0) {
      const rootAngleStep = 360 / rootChildren.length;

      rootChildren.forEach((child, index) => {
        const childAngle = 315 + index * rootAngleStep; // Start at 315° (1:30 position)
        const distance = childNodeDistance;

        const radians = (childAngle * Math.PI) / 180;
        const absolutePos = {
          x: Math.cos(radians) * distance,
          y: Math.sin(radians) * distance,
        };

        updatedPositions.set(child.id, absolutePos);

        // Layout grandchildren within this child's sector
        layoutChildren(child.id, childAngle, rootAngleStep, 1, absolutePos);
      });
    }

    // Apply all position updates
    const updatedNodes = nodes.map((node) => {
      const newPos = updatedPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  redistributeAllChildNodesRadial: (startNodeId: string) => {
    const { nodes, setNodes, childNodeDistance } = get();

    // Find the starting node
    const startNode = nodes.find((n) => n.id === startNodeId);
    if (!startNode) return;

    // Build a map of parent to children
    const childrenMap = new Map<string, Node[]>();
    nodes.forEach((node) => {
      if (node.parentId) {
        if (!childrenMap.has(node.parentId)) {
          childrenMap.set(node.parentId, []);
        }
        childrenMap.get(node.parentId)!.push(node);
      }
    });

    // Calculate depth of each node for distance scaling
    const nodeDepths = new Map<string, number>();
    const calculateDepth = (nodeId: string, depth: number) => {
      nodeDepths.set(nodeId, depth);
      const children = childrenMap.get(nodeId) || [];
      children.forEach((child) => calculateDepth(child.id, depth + 1));
    };
    calculateDepth(startNodeId, 0);

    const updatedPositions = new Map<string, { x: number; y: number }>();

    // Hierarchical radial layout with sector allocation (spreads outward from center)
    const layoutChildren = (
      parentId: string,
      parentAngle: number,
      angleSpan: number,
      depth: number
    ) => {
      const children = childrenMap.get(parentId);
      if (!children || children.length === 0) return;

      const parentNode = nodes.find((n) => n.id === parentId);
      if (!parentNode) return;

      // Distance scaling - reduced for more compact layout
      const baseDistance = childNodeDistance;
      const distance = baseDistance * Math.pow(1.4, depth) * 0.5; // More compact
      const angleStep = angleSpan / children.length;

      children.forEach((child, index) => {
        // Distribute children within their allocated angle sector
        const startAngle = parentAngle - angleSpan / 2;
        const childAngle = startAngle + (index + 0.5) * angleStep;
        const radians = (childAngle * Math.PI) / 180;

        // Calculate absolute position from center
        const absolutePos = {
          x: Math.cos(radians) * distance,
          y: Math.sin(radians) * distance,
        };

        updatedPositions.set(child.id, absolutePos);

        // Recursively layout this child's children in their subsector
        layoutChildren(child.id, childAngle, angleStep, depth + 1);
      });
    };

    // For root level, allocate full 360° divided among direct children
    const rootChildren = childrenMap.get(startNodeId);
    if (rootChildren && rootChildren.length > 0) {
      const rootAngleStep = 360 / rootChildren.length;

      rootChildren.forEach((child, index) => {
        const childAngle = 270 + index * rootAngleStep; // Start at top (270°)
        const distance = childNodeDistance * 0.5; // More compact

        const radians = (childAngle * Math.PI) / 180;
        const absolutePos = {
          x: Math.cos(radians) * distance,
          y: Math.sin(radians) * distance,
        };

        updatedPositions.set(child.id, absolutePos);

        // Layout grandchildren within this child's sector
        layoutChildren(child.id, childAngle, rootAngleStep, 1);
      });
    }

    // Apply all position updates
    const updatedNodes = nodes.map((node) => {
      const newPos = updatedPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        };
      }
      return node;
    });

    setNodes(updatedNodes);
  },
  user: null,
  setUser: (user: User | null) => set({ user }),
  toastMessage: null,
  showToast: (severity: string, summary: string, detail: string) =>
    set({ toastMessage: { severity, summary, detail } }),
  clearToast: () => set({ toastMessage: null }),
}));

export { useStore, shallow };
